import React from "react";
import { RangeSummary } from "../../../models";
import { formatMoney } from "../ReceiptsTable/receiptDisplay";
import "./RangeTotalBar.scss";

interface Props {
  summary: RangeSummary | null;
  loading?: boolean;
}

// A compact summary card shown above the receipts table in range mode: the
// range's total spend (large), the total tons dumped (when the summary carries
// a weight total — dump-yard receipts only), the receipt count, and — only when
// some rows have no extracted amount yet — an "awaiting amount" badge so a
// low-looking total is explained.
export function RangeTotalBar({ summary, loading }: Props): JSX.Element {
  if (loading) {
    return (
      <div
        className="range-total-card range-total-card--loading"
        aria-label="Loading totals"
        aria-busy="true"
      />
    );
  }

  const count = summary?.count ?? 0;
  const withAmountCount = summary?.withAmountCount ?? 0;
  const totalAmount = summary?.totalAmount ?? 0;
  const awaitingCount = count - withAmountCount;
  // totalWeight (tons) is present only for dump-yard receipts; undefined for
  // driver dump/fuel receipts, where a tons line would be meaningless.
  const totalWeight = summary?.totalWeight;

  return (
    <div className="range-total-card">
      <div className="range-total-card__label">Range total</div>
      <div className="range-total-card__amount">{formatMoney(totalAmount)}</div>
      {totalWeight != null && (
        <div className="range-total-card__weight">
          {totalWeight.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}{" "}
          tons
        </div>
      )}
      <div className="range-total-card__meta">
        <span className="range-total-card__count">
          {count} {count === 1 ? "receipt" : "receipts"}
        </span>
        {awaitingCount > 0 && (
          <span className="range-total-card__awaiting">
            {awaitingCount} awaiting amount
          </span>
        )}
      </div>
    </div>
  );
}
